import { TRM_CONSTANTS } from '../constants/trmConstants';
import { sortBy } from '../../util/lodashlike';
import {
  booleanOperators,
  datetimeOperators,
  numberOperators,
  textOperators
} from './configureInstanceDashboard/ConfigureDrawer/config';

/**
 * getStatusForScenario
 * @param statusId
 * @returns {string}
 */
export const getStatusForScenario = (statusId) => {
  if (statusId === 'InQueue') {
    return TRM_CONSTANTS.YET_TO_START;
  }
  if (statusId === 'InProgress') {
    return TRM_CONSTANTS.IN_PROGRESS;
  }
  if (statusId === 'Completed') {
    return TRM_CONSTANTS.COMPLETED;
  }
  if (statusId === 'Failed') {
    return TRM_CONSTANTS.FAILED;
  } else {
    return TRM_CONSTANTS.YET_TO_START;
  }
};

/**
 * getStrForChampion
 * @param strStatus
 * @returns {string}
 */
export const getStrForChampion = (strStatus) => strStatus;

// custom_field.data_type is the DOMAIN type (FraudDataType): integer, list_text, number, text,
// boolean, datetime, date. react-awesome-query-builder only knows text / number / date / time /
// datetime / select / multiselect / boolean, and a field whose type it cannot resolve renders
// unselectable — 42 integer v3 fields were visible in the picker but could never be used.
//
// `integer` is a real domain distinction (INT vs DOUBLE in StarRocks, IntegerDataType in the
// Scala enum), so it stays in the database and is narrowed only here, at the query-builder
// boundary. `list_text` is deliberately NOT mapped: comparing against an array needs a rule
// semantic decision, not a type alias.
const QUERY_BUILDER_TYPE = {
  integer: 'number'
};

const operatorsList = {
  boolean: booleanOperators,
  number: numberOperators,
  datetime: datetimeOperators,
  text: textOperators
};

// FIN-12586 ticket 51: the v3 block a field sits in, as a trail of display labels.
//
// `block_path` is generated into custom_field from the schema manifest and carries EVERY level —
// `transaction_event.card_context.card_intelligence`, not just `transaction_event`. The tree is
// four levels deep and two blocks hold 75% of the catalogue, so a single segment would leave two
// walls of 247 and 156 fields. Pre-v3 rows and the 55 movers keep it NULL and collapse into one
// group so the original fields don't scatter.
//
// DISPLAY ONLY. The field id stays the flat storage column; nothing may key off this trail.
export const blockLabelTrail = (blockPath) =>
  (blockPath ? String(blockPath).split('.') : ['general']).map((segment) =>
    segment
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );

export const formatQueryFields = (fields) => {
  const transaction = {};
  const activity = {};
  const groupByOptions = {
    transaction: [],
    activity: []
  };

  // The fields MAP below is keyed by field id, so a duplicated custom_field row collapses by
  // construction. groupByOptions is an array built with push, so it did not: a row duplicated
  // in custom_field produced an identical extra dropdown entry, and after the raw column moved
  // to hover there was nothing on screen to tell the copies apart. The unique key in
  // v6.4.0_custom_field.sql fixes the source; this keeps the UI honest whatever the database contains.
  const seenGroupByKey = { transaction: new Set(), activity: new Set() };
  const pushGroupByOption = (eventClass, option) => {
    if (seenGroupByKey[eventClass].has(option.value)) return;
    seenGroupByKey[eventClass].add(option.value);
    groupByOptions[eventClass].push(option);
  };

  sortBy(fields, 'uiName')?.forEach(
    ({
      name,
      dataType,
      uiName,
      actEnabled,
      txnEnabled,
      processingType,
      groupByEnabled,
      blockPath,
      apiPath
    }) => {
      const queryType = QUERY_BUILDER_TYPE[dataType] || dataType;

      // FIN-13223 — FIN-12874 made the dotted `api_path` the canonical rule identity and kept the
      // flat legacy `name` as a read-side alias (FieldCatalog.byAnyName). The UI never followed, so
      // a rule written against the canonical id rendered as an empty "Select field". Every id the
      // drawer WRITES is canonical from here on; the legacy spelling survives as a hidden alias
      // entry below so rules stored before this still resolve.
      const fieldId = apiPath || name;

      if (dataType !== 'intermediary' && !name.includes('intermediary.')) {
        if (groupByEnabled) {
          // blockTrail rides alongside the identity so the Group By dropdown can group by v3
          // block like the rule-builder picker. `legacyName` is the spelling the BE-seeded
          // `groupingKeysList` allowlist still uses — see fieldOptions.js.
          const options = {
            value: fieldId,
            legacyName: name,
            label: uiName,
            blockTrail: blockLabelTrail(blockPath)
          };

          if (actEnabled) {
            pushGroupByOption('activity', options);
          }

          if (txnEnabled) {
            pushGroupByOption('transaction', options);
          }
        }

        const options = {
          label: uiName,
          // On hover, a v3 field states where it lives in the API request
          // (`behavioural_intelligence.enrichment_source`), which is what an analyst checks the
          // client contract against and cannot read off the storage column — 484 of 540 columns
          // drop the event-class segment. Legacy rows carry no path and keep showing the column.
          //
          // Since FIN-13223 this is also the saved field id.
          tooltip: fieldId,
          // Both spellings ride on every entry so a caller holding a stored id can map it to the
          // canonical one without a second lookup table — see Functions.js `toCanonicalId`.
          canonicalName: fieldId,
          legacyName: name,
          // Display only. The config stays a FLAT map: nesting under a `!struct` parent would make
          // AQB derive the field id from the tree shape instead of the id chosen here.
          blockTrail: blockLabelTrail(blockPath),
          type: queryType,
          operators: operatorsList[queryType] || textOperators,
          ...(queryType === 'boolean' && { defaultValue: true }),
          ...(queryType === 'datetime' && { valueSources: ['value', 'func'] }),
          ...(queryType === 'number' &&
            processingType === 'default' && {
              fieldSettings: { min: 0 }
            }),
          processingType
        };

        // The legacy spelling stays in the config as a hidden twin, so a rule stored against it
        // still resolves instead of being silently dropped by Utils.checkTree. `hideForSelect`
        // keeps it out of the picker dropdown — the only id a user can newly pick is canonical.
        //
        // FIN-13640 — BOTH flags, because AQB reads them in different components and neither
        // falls back to the other: Field.jsx honours `hideForSelect` (the left-hand "Select
        // field" picker), ValueField.jsx honours `hideForCompare` (the value-source dropdown a
        // func argument or field-to-field comparison opens). With only the first, every dotted
        // `api_path` field listed twice under one ui_name the moment you picked Lowercase.
        const legacyAlias =
          fieldId === name
            ? null
            : { ...options, hideForSelect: true, hideForCompare: true };

        if (actEnabled) {
          activity[fieldId] = options;
          if (legacyAlias) activity[name] = legacyAlias;
        }

        if (txnEnabled) {
          transaction[fieldId] = options;
          if (legacyAlias) transaction[name] = legacyAlias;
        }
      }
    }
  );

  return {
    fields: {
      activity,
      transaction
    },
    groupByOptions
  };
};

// FIN-12351 — one bad `metadata_dropdown` row must not take the whole Functions tab down.
// The previous `JSON.parse(value || '')` throws on an empty string, and the throw escapes the
// reduce, so a single malformed category killed every dropdown the drawer needs. Now the row is
// dropped and the categories that did parse still render.
const parseDropdownValue = (category, value) => {
  try {
    return JSON.parse(value || 'null');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      `metadata_dropdown category "${category}" is not valid JSON; ignoring it`,
      e
    );
    return null;
  }
};

export const formatDropdownOptions = (data) => {
  const {
    aggregation_function,
    formula_function,
    screening_function,
    tooltips,
    grouping_key_pairs
    // `|| {}` matters: `data?.reduce` yields undefined for a null/absent payload, and
    // destructuring undefined throws a TypeError before any of the tolerance below can help.
  } = data?.reduce((acc, { category, value }) => {
    acc[category] = parseDropdownValue(category, value);
    return acc;
  }, {}) || {};
  const aggregateFunction = {};

  aggregation_function?.forEach(({ return_data_type, name, ui_name }) => {
    Object.keys(return_data_type).forEach((key) => {
      if (!aggregateFunction[key]) {
        aggregateFunction[key] = [];
      }

      aggregateFunction[key].push({
        value: name,
        label: ui_name,
        funcReturnType: return_data_type[key]
      });
    });
  });

  const matchMethodOptions =
    screening_function?.map(({ name, ui_name }) => ({
      value: name,
      label: ui_name
    })) || [];

  return {
    aggregateFunction,
    functionNames: formula_function,
    matchMethodOptions: sortBy(matchMethodOptions, 'label'),
    tooltips,
    // FIN-12351 — { <rule event type>: { <aggregation input type>: { <grouping key>: [value
    // fields, identity first] } } }. Absent when the seed migration has not been applied, in
    // which case every Perspective / Swap control stays hidden and behaviour is unchanged.
    groupingKeyPairs: grouping_key_pairs || {}
  };
};
